import React, { useEffect } from 'react';
import { Grid, TextField, FormControl, InputLabel, MenuItem, Select, Box } from '@mui/material';
import { formatCurrency } from 'utils/banking';

interface EarlyDiscountFieldsProps {
  values: any;
  touched: any;
  errors: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
}

const formatPercent = (value: string) => {
  const digits = value.replace(/\D/g, '');

  if (!digits) return '';

  let number = Number(digits) / 100;

  if (number > 100) {
    number = 100;
  }

  return `${number.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const EarlyDiscountFields: React.FC<EarlyDiscountFieldsProps> = ({ values, touched, errors, handleChange, handleBlur }) => {
  const handlePercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const digits = value.replace(/\D/g, '');

    const formatted = digits ? formatPercent(digits) : '';

    handleChange({
      ...e,
      target: {
        ...e.target,
        name,
        value: formatted,
      },
    });
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const formatted = formatCurrency(value);

    handleChange({
      ...e,
      target: {
        ...e.target,
        name,
        value: formatted,
      },
    });
  };

  useEffect(() => {
    if (!values.earlyDiscountValue) return;

    let formatted = values.earlyDiscountValue;

    if (values.earlyDiscountType === 'percentual') {
      formatted = formatPercent(values.earlyDiscountValue);
    } else if (values.earlyDiscountType === 'fixo') {
      formatted = formatCurrency(values.earlyDiscountValue);
    }

    if (formatted !== values.earlyDiscountValue) {
      handleChange({
        target: {
          name: 'earlyDiscountValue',
          value: formatted,
        },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [values.earlyDiscountType, values.earlyDiscountValue]);

  const isPercent = values.earlyDiscountType === 'percentual';

  return (
    <Box pr={1.5} pb={1.5}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth required variant="standard">
            <InputLabel shrink sx={{ fontSize: '16px', fontWeight: 500 }}>
              Tipo de desconto
            </InputLabel>

            <Select
              label="Tipo de desconto"
              name="earlyDiscountType"
              value={values.earlyDiscountType}
              onChange={(event) => handleChange(event as React.ChangeEvent<HTMLInputElement>)}
              onBlur={handleBlur}
              error={Boolean(touched.earlyDiscountType && errors.earlyDiscountType)}
              sx={{ fontSize: '14px' }}
            >
              <MenuItem value="fixo">Valor fixo</MenuItem>
              <MenuItem value="percentual">Percentual</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          {isPercent ? (
            <TextField
              label="Valor"
              type="text"
              placeholder="Informe um valor percentual"
              fullWidth
              required
              variant="standard"
              name="earlyDiscountValue"
              value={values.earlyDiscountValue}
              onChange={handlePercentChange}
              onBlur={handleBlur}
              error={Boolean(touched.earlyDiscountValue && errors.earlyDiscountValue)}
              helperText={touched.earlyDiscountValue && errors.earlyDiscountValue}
              InputLabelProps={{
                shrink: true,
                sx: { fontSize: '16px', fontWeight: 500 },
              }}
              InputProps={{ sx: { fontSize: '14px' } }}
              disabled={!values.earlyDiscountType}
            />
          ) : (
            <TextField
              label="Valor"
              placeholder="Informe um valor fixo em Reais"
              fullWidth
              required
              variant="standard"
              name="earlyDiscountValue"
              value={values.earlyDiscountValue}
              onChange={handleCurrencyChange}
              onBlur={handleBlur}
              error={Boolean(touched.earlyDiscountValue && errors.earlyDiscountValue)}
              helperText={touched.earlyDiscountValue && errors.earlyDiscountValue}
              InputLabelProps={{
                shrink: true,
                sx: { fontSize: '16px', fontWeight: 500 },
              }}
              InputProps={{ sx: { fontSize: '14px' } }}
              disabled={!values.earlyDiscountType}
            />
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Máximo de dias permitido"
            type="number"
            fullWidth
            required
            variant="standard"
            name="earlyDiscountDays"
            value={values.earlyDiscountDays}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched.earlyDiscountDays && errors.earlyDiscountDays)}
            helperText={touched.earlyDiscountDays && errors.earlyDiscountDays}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: '16px', fontWeight: 500 },
            }}
            InputProps={{ sx: { fontSize: '14px' } }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default EarlyDiscountFields;
